import { observer } from "mobx-react-lite";
import * as React from "react";
import { ReactSVG } from "react-svg";
import { usePassportStore } from "src/stores/Passport.store";
import { Button } from "src/components";
import { Box } from "src/components/Box";
import { Text } from "src/components/Text";
import { MediaQueries } from "src/constants";
import { fromEnv } from "src/helpers";
import { useTranslation } from "src/hooks";
import styled, { css } from "styled-components";
import { PassportAssets } from "src/assets";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { useEffect } from "react";

const BoxEl = styled.div`
  ${MediaQueries.screen.medium.minWidth} {
    display: flex;
    flex-direction: column;
  }
`;

const ContainerEl = styled.div`
  display: flex;
  flex-direction: column;
  ${MediaQueries.screen.medium.minWidth} {
    max-width: ${(p) => p.theme.dimension.width.smallContainer}px;
    align-self: center;
  }
`;

const IconContainerEl = styled.div`
  align-self: center;
  ${(p) => css`
    margin-bottom: ${p.theme.layout.margin.s}px;
    margin-top: ${p.theme.layout.margin.l}px;
  `}
  ${MediaQueries.screen.medium.minWidth} {
    margin-top: 0;
  }
`;

const PaymentMethodEl = styled.div`
  display: flex;
  margin-bottom: ${(p) => p.theme.layout.margin.l}px;
`;

const SubContainerEl = styled.div<{ isFromWhatsapp?: boolean }>`
  ${(p) =>
    p.isFromWhatsapp &&
    css`
      background: ${(p) => p.theme.colors.background.primary};
    `}
  ${(p) =>
    css`
      border-radius: ${p.theme.dimension.borderRadius[2]}px;
      margin-top: ${p.theme.dimension.spacing.xxl}px;
    `}
`;

const TextContainerEl = styled.div`
  width: 100%;
  ${(p) => css`
    margin-left: ${p.theme.layout.margin.l}px;
    padding-bottom: ${p.theme.layout.margin.l}px;
    border-bottom: 1px solid ${p.theme.colors.borders.appBar};
  `}
`;

const WhatsappContainerEl = styled.div`
  padding: ${(p) => p.theme.dimension.spacing.l}px;
`;

const WrapperEl = styled.div<{ isFromWhatsapp?: boolean }>`
  background: ${(p) => p.theme.colors.background.white};
  ${(p) =>
    p.isFromWhatsapp &&
    css`
      border: 1px solid ${p.theme.colors.background.primary};
      border-radius: ${p.theme.dimension.borderRadius[2]}px;
      padding: ${p.theme.dimension.spacing.l}px ${p.theme.dimension.spacing.l}px
        0;
    `}
`;

export const PassportOnboardingCompletedScreen = observer(() => {
  const redirectToHera = () => {
    eventDispatcher.publish({ action: "ob_finish_dashboard_clicked" });
    window.location.href = fromEnv("REACT_APP_HERA_HOST");
  };
  const redirectToPaymentLink = () => {
    eventDispatcher.publish({ action: "ob_finish_pl_clicked" });
    window.location.href = `${fromEnv("REACT_APP_HERA_HOST")}/snap_links`;
  };
  const translate = useTranslation();
  const { outlet, isCardPayment } = usePassportStore();
  const isFromWhatsapp = outlet.origin === "whatsapp";

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_finish_opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PaymentMethodItems = () => {
    return (
      <Box marginBottomXxl>
        <PaymentMethodEl>
          <ReactSVG src={PassportAssets.success.BankOthers} />
          <TextContainerEl>
            <Box flex justifyBetween>
              <Text>
                {translate("onboarding_completed.payment_method.basic")}
              </Text>
              {!isFromWhatsapp && (
                <Button
                  outline
                  size="xs"
                  onClick={() => redirectToPaymentLink()}
                >
                  {translate("onboarding_completed.cta.payment_link")}
                </Button>
              )}
            </Box>
            <Box marginTopS>
              <Text
                fontSize="medium"
                color="warning"
                whiteSpace="nowrap"
                as="span"
              >
                {translate("onboarding_completed.status.on_process")}
              </Text>
              <Text fontSize="medium" as="span">
                &nbsp; - &nbsp;
                {translate("onboarding_completed.time_estimation.basic")}
              </Text>
            </Box>
          </TextContainerEl>
        </PaymentMethodEl>
        {isCardPayment && (
          <PaymentMethodEl>
            <ReactSVG src={PassportAssets.success.CardChecklist} />
            <TextContainerEl>
              <Text>{translate("onboarding_completed.payment_method.cc")}</Text>
              <Box marginTopS>
                <Text
                  fontSize="medium"
                  color="warning"
                  whiteSpace="nowrap"
                  as="span"
                >
                  {translate("onboarding_completed.status.on_process")}
                </Text>
                <Text fontSize="medium" as="span">
                  &nbsp; - &nbsp;
                  {translate("onboarding_completed.time_estimation.cc")}
                </Text>
              </Box>
            </TextContainerEl>
          </PaymentMethodEl>
        )}
      </Box>
    );
  };

  return (
    <BoxEl>
      <ContainerEl>
        <IconContainerEl>
          <ReactSVG src={PassportAssets.success.Success} />
        </IconContainerEl>
        <SubContainerEl isFromWhatsapp={isFromWhatsapp}>
          <WrapperEl isFromWhatsapp={isFromWhatsapp}>
            <Text marginBottomS fontSize="mlarge" fontWeight="demi">
              {translate("onboarding_completed.title")}
            </Text>
            <Text marginBottomXxl>
              {translate("onboarding_completed.description")}
            </Text>
            <PaymentMethodItems />
          </WrapperEl>
          {isFromWhatsapp ? (
            <WhatsappContainerEl>
              <Text fontWeight="demi" color="white">
                {translate("onboarding_completed.whatsapp")}
              </Text>
            </WhatsappContainerEl>
          ) : (
            <Button stretch onClick={() => redirectToHera()}>
              {translate("onboarding_completed.cta.positive")}
            </Button>
          )}
        </SubContainerEl>
      </ContainerEl>
    </BoxEl>
  );
});
